<template>
	<v-expand-transition>
		<div v-if="open && quickTags.length" class="context-quick-tags">
			<div v-for="(tag, index) of quickTags" :key="tag.contextId" class="context-quick-tags__item">

				<div class="context-list-item__droppable">
					<draggable :id="zone+'-before-'+tag.contextId"
							   :item="tag.contextId"
							   :draggable="false"
							   :droppable="true"
							   :accepts-zones="[zone]"
							   :rejects-ids="rejectIdsFor(index)"
							   :preference="1"
							   :zone="zone"
							   class="context-list-item__droppable-zone"
							   :drop-processor="onDrop"/>
				</div>

				<draggable :id="zone+'-'+tag.contextId"
						   :item="tag.contextId"
						   :zone="zone"
						   :draggable="true"
						   :droppable="false">
					<template v-slot:default="{onMouseDown}">
						<div class="context-quick-tags__element" :class="{'--selected': isSelected(tag)}">
							<router-link :to="routeOfTag(tag)" draggable="false" class="context-quick-tags__link"
										 @click.native="onClickTag">
								<v-icon x-small :color="colorOfTag(tag)" class="context-quick-tags__icon">mdi-pound</v-icon>
								<span class="context-quick-tags__name">{{ tag.name }}</span>
							</router-link>

							<v-btn icon small class="context-quick-tags__btn" title="Открепить"
								   :loading="unpinningId === tag.contextId"
								   @click.prevent.stop="onUnpin(tag)">
								<v-icon small>mdi-pin-off-outline</v-icon>
							</v-btn>

							<div class="draggable__handle" @mousedown.prevent.stop="onMouseDown">
								<v-icon>mdi-drag-vertical</v-icon>
							</div>
						</div>
					</template>
				</draggable>
			</div>

			<div class="context-list-item__droppable">
				<draggable :id="zone+'-last'"
						   item="last"
						   :draggable="false"
						   :droppable="true"
						   :accepts-zones="[zone]"
						   :rejects-ids="rejectIdsFor(quickTags.length)"
						   :preference="1"
						   :zone="zone"
						   class="context-list-item__droppable-zone"
						   :drop-processor="onDrop"/>
			</div>

			<v-btn v-if="$route.query?.withAddTag" small block text tile class="context-quick-tags__add" @click="onAdd">
				<v-icon small left>mdi-plus</v-icon>
				Добавить тег
			</v-btn>
		</div>
	</v-expand-transition>
</template>

<script>
import Config from "@/config";
import base from "@/mixins/base";
import vp from "@/mixins/vp";
import draggable from "@/components/elements/draggable2";

/**
 * Быстрые теги проекта: сворачиваемый список под элементом группы в дереве контекстов.
 * Ссылки ведут на тот же роут context с query ?tag=<id>. Порядок и открепление - только через сервер.
 */
export default {
	name: "context-quick-tags",
	components: {draggable},
	mixins: [base, vp],
	props: {
		/** Группа (проект) с полем quickTags */
		item: {type: Object, required: true},
		/** Список раскрыт */
		open: Boolean,
	},
	data: () => ({
		unpinningId: null,
	}),
	computed: {
		quickTags() {
			return this.item.quickTags || [];
		},
		/** Зона drag-n-drop: перетаскивать можно только внутри одной группы */
		zone() {
			return "quick-tags-" + this.item.id;
		},
	},
	methods: {
		routeOfTag(tag) {
			return {
				name: "context",
				params: {contextId: this.item.contextId || this.item.id},
				query: {tag: tag.contextId},
			};
		},
		colorOfTag(tag) {
			return tag.color ? `var(${tag.color})` : undefined;
		},
		isSelected(tag) {
			return this.$route.query.tag === tag.contextId && this.context?.item?.id === this.item.id;
		},
		/** Не даём бросить тег на него самого или на позицию, где он уже стоит */
		rejectIdsFor(index) {
			const rejectIds = [];
			const tag = this.quickTags[index];
			if (tag) rejectIds.push(tag.contextId);
			const prev = index ? this.quickTags[index - 1] : null;
			if (prev) rejectIds.push(prev.contextId);
			return rejectIds;
		},
		onClickTag() {
			// на мобильном скрываем список контекстов после перехода
			if (!this.isMD) this.commit("uiToggleContextList");
		},
		/** Новый порядок сохраняется на сервере, список обновляется по contextUpdate из ответа */
		onDrop(source, destination) {
			const from = source.item;
			const before = destination.item;
			const ids = this.quickTags.map(el => el.contextId).filter(id => id !== from);
			const index = before === "last" ? ids.length : ids.indexOf(before);
			ids.splice(index < 0 ? ids.length : index, 0, from);
			return this.do("context/setQuickTags", {groupId: this.item.id, tagIds: ids, ordered: true});
		},
		/** Открепление сразу на сервере, без оптимистичного обновления */
		async onUnpin(tag) {
			this.unpinningId = tag.contextId;
			try {
				await this.do("context/unpinQuickTag", {groupId: this.item.id, tagId: tag.contextId});
			} catch (err) {
				console.warn("error", err);
			} finally {
				this.unpinningId = null;
			}
		},
		onAdd() {
			this.$store.dispatch("ui/dialogGroup", {item: this.item, tabIndex: Config.DIALOG_GROUP_TABS.QUICK_TAGS});
		},
	},
}
</script>

<style lang="scss">
.context-quick-tags {
	padding-left: calc(#{$general-padding} * 2 + 2px);

	&__item {
		position: relative;
	}

	&__element {
		height: 34px;	// we take same as seems at context-list-item
		position: relative;
		display: flex;
		align-items: center;
		padding: 0 $min-padding 0 calc(#{$general-padding} + 22px); // +8px - отступ от ручки перетаскивания
		//border-radius: 0 $general-border-radius $general-border-radius 0;
		@include transition();

		&:hover {
			background: map-get($grey, "lighten-3");

			.context-quick-tags__btn {
				opacity: 1;
			}
		}

		&.--selected {
			//background: map-get($grey, "lighten-5");
			background: white;
		}
	}

	&__link {
		flex: 1;
		min-width: 0;
		display: flex;
		align-items: center;
		gap: $min-padding;
		color: $default-text-color !important;
		//line-height: 1.3rem;
		// та же высота строки, что у элементов context-list-item (.__name: padding 8px 0)
		//padding: 8px 0;
	}

	&__name {
		@include text-overflow();
		padding-bottom: 2px;
	}

	&__icon {
		margin-top: 2px;
	}

	&__btn {
		opacity: 1;

		@include up($sm) {
			opacity: 0;
		}
	}

	.draggable__handle {
		left: 2px !important;
	}

	&__add.v-btn {
		//margin: 2px 0;
		margin-top: 8px;
		justify-content: flex-start;
		.v-btn__content {
			padding-left: 12px;
		}
	}
}
</style>
